import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send, Loader2, Phone } from "lucide-react";
import { trpc } from "@/lib/trpc";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { trackChatSubmission } from "@/lib/googleAds";

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

// Quick reply suggestions shown after the welcome message
const QUICK_REPLIES = [
  "Interior painting quote",
  "Exterior painting quote",
  "Cabinet refinishing",
  "Roof coating & repair",
  "Venetian plaster",
  "Commercial painting",
];

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const chatMutation = trpc.chat.sendMessage.useMutation({
    onSuccess: (data) => {
      if (data.message) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.message,
            timestamp: Date.now(),
          },
        ]);
        // Hide quick replies after first AI response
        setShowQuickReplies(false);
      }
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Listen for openChat event from buttons
  useEffect(() => {
    const handleOpenChat = () => {
      setIsOpen(true);
    };
    window.addEventListener('openChat', handleOpenChat);
    return () => window.removeEventListener('openChat', handleOpenChat);
  }, []);

  // Add welcome message when chat opens for the first time
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          role: "assistant",
          content: "Hi! 👋 Welcome to **Danis Painting Co.** I'm here to help you get a **free estimate** for your painting or property maintenance project.\n\nWhat can I help you with today?",
          timestamp: Date.now(),
        },
      ]);
      // Show quick reply chips after welcome message
      setShowQuickReplies(true);
    }
  }, [isOpen, messages.length]);

  const sendMessage = (content: string) => {
    if (!content.trim() || chatMutation.isPending) return;

    const userMessage: Message = {
      role: "user",
      content: content.trim(),
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setShowQuickReplies(false);

    chatMutation.mutate({
      sessionId,
      message: userMessage.content,
      conversationHistory: messages.map((m) => ({
        role: m.role,
        content: m.content,
      })),
    });
    
    // Track chat submission for Google Ads
    trackChatSubmission();
  };

  const handleSend = () => sendMessage(input);

  const handleQuickReply = (text: string) => sendMessage(text);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Reactively detect mobile so positioning updates on resize.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  // Track whether the sticky call bar is currently visible (same threshold as StickyCallButton).
  const [callBarVisible, setCallBarVisible] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const mqHandler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', mqHandler);
    setIsMobile(mq.matches);

    const threshold = window.innerHeight * 0.8;
    const onScroll = () => setCallBarVisible(window.scrollY > threshold);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // initialise on mount

    return () => {
      mq.removeEventListener('change', mqHandler);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Mobile positioning:
  //   • Call bar hidden  → sit 16px above the bottom edge (always visible in corner)
  //   • Call bar visible → sit 12px above the call bar (56px + safe-area)
  // Desktop: standard 24px corner offset.
  const callBarHeight = 'calc(56px + env(safe-area-inset-bottom, 0px))';
  const chatBottom = isMobile
    ? (callBarVisible ? `calc(${callBarHeight} + 12px)` : '16px')
    : '24px';
  const chatRight = isMobile ? '12px' : '24px';

  return createPortal(
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed z-[9999] w-14 h-14 rounded-full bg-primary text-white shadow-lg hover:bg-primary/90 transition-all duration-300 flex items-center justify-center ${
          isOpen ? "scale-0" : "scale-100"
        }`}
        style={{ bottom: chatBottom, right: chatRight }}
        aria-label="Open chat"
      >
        <div className="relative w-full h-full flex items-center justify-center">
          <MessageCircle className="w-6 h-6" />
          {/* Green online indicator dot */}
          <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 border-2 border-white rounded-full" aria-hidden="true">
            <span className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-75" />
          </span>
        </div>
      </button>

      {/* Chat Window */}
      <div
        className={`fixed z-[9999] w-[380px] max-w-[calc(100vw-2rem)] transition-all duration-300 ${
          isOpen ? "scale-100 opacity-100" : "scale-95 opacity-0 pointer-events-none"
        }`}
        style={{ bottom: chatBottom, right: chatRight }}
      >
        <Card className="shadow-2xl border-border/50 overflow-hidden">
          {/* Header */}
          <CardHeader className="bg-[#0a1628] text-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#65ed47]/20 border border-[#65ed47]/40 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-[#65ed47]" />
                </div>
                <div>
                  <CardTitle className="text-base font-semibold">Danis Painting Co.</CardTitle>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="w-2 h-2 rounded-full bg-[#65ed47] animate-pulse" />
                    <p className="text-white/70 text-xs">AI Assistant • Replies instantly</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href="tel:805-403-8727"
                  className="text-white/70 hover:text-[#65ed47] transition-colors p-1.5 rounded-full hover:bg-white/10"
                  aria-label="Call us"
                  title="Call 805-403-8727"
                >
                  <Phone className="w-4 h-4" />
                </a>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-white/70 hover:text-white transition-colors p-1.5 rounded-full hover:bg-white/10"
                  aria-label="Close chat"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {/* Messages */}
            <div className="h-[320px] overflow-y-auto p-4 space-y-3 bg-gray-50">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {message.role === "assistant" && (
                    <div className="w-7 h-7 rounded-full bg-[#0a1628] flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
                      <MessageCircle className="w-3.5 h-3.5 text-[#65ed47]" />
                    </div>
                  )}
                  <div
                    className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm ${
                      message.role === "user"
                        ? "bg-[#0a1628] text-white rounded-tr-sm"
                        : "bg-white text-gray-800 border border-gray-200 shadow-sm rounded-tl-sm"
                    }`}
                  >
                    {message.role === "assistant" ? (
                      <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {message.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    )}
                  </div>
                </div>
              ))}

              {/* Quick Reply Chips */}
              {showQuickReplies && !chatMutation.isPending && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {QUICK_REPLIES.map((reply) => (
                    <button
                      key={reply}
                      onClick={() => handleQuickReply(reply)}
                      className="text-xs px-3 py-1.5 rounded-full border border-[#0a1628]/20 bg-white hover:bg-[#0a1628] hover:text-white hover:border-[#0a1628] transition-all duration-200 text-gray-700 shadow-sm"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              )}

              {chatMutation.isPending && (
                <div className="flex justify-start">
                  <div className="w-7 h-7 rounded-full bg-[#0a1628] flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
                    <MessageCircle className="w-3.5 h-3.5 text-[#65ed47]" />
                  </div>
                  <div className="bg-white border border-gray-200 shadow-sm rounded-2xl rounded-tl-sm px-4 py-3">
                    <div className="flex gap-1 items-center">
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-gray-200 bg-white">
              <div className="flex gap-2 items-center">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Describe your project..."
                  className="flex-1 text-sm border-gray-200 focus:border-[#0a1628] rounded-full px-4"
                  disabled={chatMutation.isPending}
                />
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || chatMutation.isPending}
                  size="icon"
                  className="bg-[#0a1628] hover:bg-[#0a1628]/90 rounded-full w-9 h-9 flex-shrink-0"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-center text-xs text-gray-400 mt-2">
                Or call us directly:{" "}
                <a href="tel:805-403-8727" className="text-[#0a1628] font-medium hover:underline">
                  805-403-8727
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </>,
    document.body
  );
}
